import auth from '@/api/auth'
import { getContractId, getMembershipFee } from '@/api/myMembership'
import { getUserAccountInfo } from '@/api/myProfile'

function defaultUserState() {
  return {
    user: false,
    sessionData: false,
    membershipFee: null,
    // TODO: contractId seems not to be used
    contractId: null,
    userAccountInfo: null,
  }
}

export default {
  state: () => ({
    ...defaultUserState(),
    isMobile: false,
  }),
  actions: {
    async getUserSessionData({ commit }) {
      try {
        const sessionData = await auth.getSessionData()
        commit('setSessionData', sessionData.data)
      } catch (error) {
        console.error(error)
      }
    },
    async getMembershipFee({ commit, state }) {
      try {
        const response = await getMembershipFee(state.user.pnr)
        commit('setMembershipFee', response.data)
      } catch (error) {
        console.error(error)
      }
    },
    async getContractId({ commit, state }) {
      try {
        const response = await getContractId(state.user.pnr)
        commit('setContractId', response.data[0].contractId)
      } catch (error) {
        console.error(error)
      }
    },
    async getUserAccountInfo({ commit, state }) {
      try {
        const response = await getUserAccountInfo(state.user.pnr)
        commit('setUserAccountInfo', response.data)
        commit('setUser', {
          firstname: response.data.firstName,
          lastname: response.data.lastName,
          pnr: response.data.cknr,
          isMember: response.data.isMember,
          isCompany: response.data.isCompany,
          email: response.data.email.email,
        })
      } catch (error) {
        console.error(error)
      }
    },
  },
  mutations: {
    setIsMobile(state, payload) {
      state.isMobile = payload
    },
    resetUserState(state) {
      Object.assign(state, {
        ...state,
        ...defaultUserState(),
      })
    },
    setUser(state, payload) {
      state.user = payload
    },
    setSessionData(state, payload) {
      state.sessionData = payload
    },
    setMembershipFee(state, payload) {
      state.membershipFee = payload
    },
    setContractId(state, payload) {
      state.contractId = payload
    },
    setUserAccountInfo(state, payload) {
      state.userAccountInfo = payload
    },
  },
  getters: {
    isMobile(state) {
      return state.isMobile
    },
    user(state) {
      return state.user
    },
    sessionData(state) {
      return state.sessionData
    },
    membershipFee(state) {
      const membership = +state.membershipFee?.membership.offensaldo?.replace(',', '.') || 0

      return membership.toFixed(2).toString()
    },
    /**
     * Determines if the user has paid their membership fees.
     * Returns true when fee information is not yet available.
     * Once fee information is loaded, returns true only if the user is a member,
     * their membership is marked as paid, and they have no outstanding balance.
     * @returns {boolean} Whether the user has paid or fee info is not yet available
     */
    userHasPaid(state, getters) {
      const isMember = state.user?.isMember || false

      // If the user is not a member, they don't need to pay
      if (!isMember) return true

      // If fee information is not available yet, assume paid (return true) to avoid info message showing to early
      if (!state.membershipFee) return true

      // Once fee info is available, do the actual calculation
      return state.membershipFee?.membership.isPayed && +getters.membershipFee === 0
    },
    /**
     * Retrieves the user account information from the state.
     *
     * @param {Object} state - The Vuex state.
     * @returns {UserAccountInfo | null} The user account information object, or null if not available.
     */
    userAccountInfo(state) {
      return state.userAccountInfo
    },
  },
}

/**
 * @typedef {Object} UserAccountInfo
 * @property {Object} address - The address of the user.
 * @property {string} address.city - The city of the user.
 * @property {string} address.country - The country of the user.
 * @property {string} address.street - The street of the user.
 * @property {string} address.streetNumber - The street number of the user.
 * @property {string} address.zip - The zip code of the user.
 * @property {string} address.addressSupplement - The address supplement of the user.
 * @property {string} address.extAdrNr - The external address number of the user.
 * @property {string} dateOfBirth - The date of birth of the user.
 * @property {string|null} title - The title of the user.
 * @property {string|null} titleBefore - The title before the name of the user.
 * @property {string|null} titleAfter - The title after the name of the user.
 * @property {string|null} job - The job of the user.
 * @property {string} salutation - The salutation of the user.
 * @property {string} firstName - The first name of the user.
 * @property {number} cknr - The customer number of the user.
 * @property {Object} email - The email information of the user.
 * @property {string} email.email - The email address of the user.
 * @property {string|null} email.status - The status of the email address.
 * @property {boolean|null} email.inChange - Whether the email is in change.
 * @property {string|null} email.emailInChange - The email address in change.
 * @property {string|null} phone - The phone number of the user.
 * @property {string} lastName - The last name of the user.
 * @property {'1'|'4'|'5'|'6'|'7'|'8'|'9'|null} lv - The landesverein (regional association) of the user.
 *   Can be null if no prediction can be made.
 *   Possible values and their meanings:
 *   - `null`: Landesverein konnte nicht ermittelt werden
 *   - `'1'`: Wien, Niederösterreich und Burgenland
 *   - `'4'`: Oberösterreich
 *   - `'5'`: Salzburg
 *   - `'6'`: Tirol
 *   - `'7'`: Vorarlberg
 *   - `'8'`: Steiermark
 *   - `'9'`: Kärnten
 */
